import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Select, { components as selectComponents } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import debounce from 'lodash/debounce';
import { Avatar } from './AvatarGroup';
import { ICONS } from '../../constants/icons';
import { get } from '../../api/axios';
import endpoints from '../../api/endpoints';
import { getUserAvatarUrl } from '../../helpers/userAvatar';
import { fetchMyFlaggedMembers } from '../../pages/Flags/flagData';

const SEARCH_DEBOUNCE_MS = 300;
const USER_SEARCH_PAGE_SIZE = 10;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isUuid = value => UUID_PATTERN.test(String(value || '').trim());

const getUserId = user =>
  user?.id ||
  user?.user_id ||
  user?.uuid ||
  user?.ulid ||
  user?.user?.id ||
  '';

const asUuid = value => {
  const normalized = String(value || '').trim();
  return isUuid(normalized) ? normalized : '';
};

/**
 * Resolve a real user UUID from a select option.
 * Invitation / membership rows expose both `id` (row) and `user_id` / `user.id`
 * — never send the row id or an email as user_ids.
 */
export const getCollaboratorUserId = item => {
  const raw = item?.raw;
  if (raw && typeof raw === 'object') {
    const nestedUserId =
      raw.user_id ||
      raw.userId ||
      raw.user?.id ||
      raw.user?.user_id ||
      null;
    const nestedUuid = asUuid(nestedUserId);
    if (nestedUuid) return nestedUuid;

    // Plain user object (no invitation wrapper)
    if (!raw.user) {
      const plainUuid = asUuid(raw.id || raw.uuid || raw.ulid);
      if (plainUuid) return plainUuid;
    }
  }

  return asUuid(item?.value);
};

const isValidInviteEmail = value =>
  EMAIL_PATTERN.test(String(value || '').trim());

const isCurrentUser = (user, currentUser) => {
  if (!currentUser) return false;
  const currentId = currentUser.id != null ? String(currentUser.id) : '';
  const currentEmail = String(currentUser.email || '').trim().toLowerCase();
  const userId = getUserId(user) ? String(getUserId(user)) : '';
  const userEmail = String(user?.email || '').trim().toLowerCase();

  if (currentId && userId && currentId === userId) return true;
  if (currentEmail && userEmail && currentEmail === userEmail) return true;
  return false;
};

const isUserFlaggedByMe = user => {
  const values = [
    user?.is_flagged_by_me,
    user?.flagged_by_me,
    user?.you_have_flagged,
    user?.has_flagged,
    user?.is_flagged_by_current_user,
    user?.flagged,
    user?.is_flagged,
    user?.has_active_flag,
  ]
  return values.some(
    value => value === true || value === 1 || value === '1' || value === 'true'
  )
}

export const mapUserToCollabOption = (user, { isFlagged = false } = {}) => {
  const name =
    user?.full_name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() ||
    user?.name ||
    '';
  const email = user?.email || '';
  const userId = asUuid(getUserId(user));
  const flagged = Boolean(isFlagged || isUserFlaggedByMe(user));
  return {
    value: userId || email,
    label: name ? `${name}${email ? ` (${email})` : ''}` : email || 'Unknown',
    email,
    name: name || email || 'Unknown',
    avatar: getUserAvatarUrl(user),
    raw: user,
    isFlagged: flagged,
    isDisabled: flagged,
  };
};

export const mapEmailToCollabOption = email => {
  const normalized = String(email || '').trim().toLowerCase();
  return {
    value: normalized,
    label: normalized,
    email: normalized,
    name: normalized,
    avatar: null,
    __isNew__: true,
  };
};

export const collabSelectStyles = (hasError = false, menuZIndex = 10060) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 30,
    border: 'none',
    borderRadius: 12,
    backgroundColor: 'transparent',
    boxShadow: 'none',
    cursor: 'text',
    paddingLeft: 0,
    borderColor: hasError ? '#9b0000' : 'transparent',
    '&:hover': { border: 'none', backgroundColor: 'transparent' },
    ...(state.isFocused ? { outline: 'none' } : {}),
  }),
  valueContainer: base => ({
    ...base,
    padding: '0',
    gap: 6,
    minHeight: 30,
  }),
  container: base => ({
    ...base,
    width: '100%',
  }),
  input: base => ({
    ...base,
    margin: 0,
    padding: 0,
    color: '#0d0d0d',
    fontSize: 14,
    fontWeight: 500,
  }),
  placeholder: base => ({
    ...base,
    margin: 0,
    color: 'rgba(110, 110, 110, 0.7)',
    fontSize: 14,
    fontWeight: 500,
  }),
  singleValue: base => ({
    ...base,
    margin: 0,
    color: '#0d0d0d',
    fontSize: 14,
    fontWeight: 500,
  }),
  multiValue: base => ({
    ...base,
    margin: 0,
    backgroundColor: '#ecf0fa',
    borderRadius: 999,
  }),
  multiValueLabel: base => ({
    ...base,
    padding: '2px 4px 2px 2px',
  }),
  multiValueRemove: base => ({
    ...base,
    borderRadius: 999,
    color: '#6e6e6e',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: 'rgba(155, 0, 0, 0.08)',
      color: '#9b0000',
    },
  }),
  indicatorsContainer: base => ({
    ...base,
    height: 30,
    alignSelf: 'center',
  }),
  clearIndicator: base => ({
    ...base,
    padding: 4,
  }),
  dropdownIndicator: () => ({ display: 'none' }),
  indicatorSeparator: () => ({ display: 'none' }),
  menu: base => ({
    ...base,
    marginTop: 8,
    borderRadius: 12,
    boxShadow: '0 8px 24px rgba(17, 17, 17, 0.08)',
    overflow: 'hidden',
    zIndex: menuZIndex,
  }),
  menuPortal: base => ({ ...base, zIndex: menuZIndex }),
  menuList: base => ({
    ...base,
    padding: 4,
    maxHeight: 220,
  }),
  option: (base, state) => ({
    ...base,
    padding: '8px 10px',
    borderRadius: 8,
    backgroundColor:
      state.isFocused || state.isSelected ? '#f2f2f4' : 'transparent',
    color: state.isDisabled ? '#9b9b9b' : '#0d0d0d',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    opacity: state.isDisabled ? 0.72 : 1,
    '&:active': {
      backgroundColor: state.isDisabled ? 'transparent' : '#e7e7ea',
    },
  }),
  noOptionsMessage: base => ({
    ...base,
    fontSize: 13,
    color: '#b7b7b7',
  }),
  loadingMessage: base => ({
    ...base,
    fontSize: 13,
    color: '#b7b7b7',
  }),
});

const CollaboratorOption = props => {
  const { data } = props;
  if (data?.__isNew__) {
    return (
      <selectComponents.Option {...props}>
        <span className="project-collab-modal__option">
          <span className="project-collab-modal__option-meta">
            <span className="project-collab-modal__option-name">
              Invite &quot;{data.email || data.value}&quot;
            </span>
            <span className="project-collab-modal__option-email">
              Add custom email address
            </span>
          </span>
        </span>
      </selectComponents.Option>
    );
  }

  return (
    <selectComponents.Option {...props}>
      <span
        className={`project-collab-modal__option${
          data?.isFlagged || data?.isDisabled || props.isDisabled
            ? ' project-collab-modal__option--flagged'
            : ''
        }`}
      >
        <Avatar
          src={data.avatar}
          name={data.name}
          size="xs"
          className="project-collab-modal__option-avatar"
        />
        <span className="project-collab-modal__option-meta">
          <span className="project-collab-modal__option-name-row">
            <span className="project-collab-modal__option-name">{data.name}</span>
            {data?.isFlagged || props.isDisabled ? (
              <span className="project-collab-modal__option-flagged">Flagged</span>
            ) : null}
          </span>
          {data.email ? (
            <span className="project-collab-modal__option-email">{data.email}</span>
          ) : null}
        </span>
      </span>
    </selectComponents.Option>
  );
};

const CollaboratorMultiValueLabel = props => {
  const { data } = props;
  return (
    <selectComponents.MultiValueLabel {...props}>
      <span className="project-collab-modal__chip">
        <Avatar
          src={data.avatar}
          name={data.name}
          size="xs"
          className="project-collab-modal__chip-avatar"
        />
        <span className="project-collab-modal__chip-name">{data.name}</span>
      </span>
    </selectComponents.MultiValueLabel>
  );
};

/**
 * Invite-Collaborator search: city users via GET /api/v1/users/:cityId,
 * multi-select chips, optional email create, infinite scroll.
 */
const CollaboratorSearchSelect = ({
  cityId,
  value = [],
  onChange,
  onBlur,
  disabled = false,
  disabledReason = null,
  error = null,
  enabled = true,
  label = 'Search Collaborator',
  placeholder = 'Search by email or username...',
  inputId = 'collab-search-select',
  className = '',
  labelClassName = '',
  allowCreate = true,
  skipCityCheck = false,
  menuZIndex = 10060,
  excludeUserIds = [],
  excludeEmails = [],
  flaggedUserIds: flaggedUserIdsProp,
  flaggedEmails: flaggedEmailsProp,
}) => {
  const currentUser = useSelector(state => state.auth?.currentUser);
  const currentUserId = currentUser?.id != null ? String(currentUser.id) : '';
  const currentUserEmail = String(currentUser?.email || '')
    .trim()
    .toLowerCase();

  const excludedIdSet = useMemo(
    () =>
      new Set(
        (excludeUserIds || [])
          .map(id => String(id || '').trim().toLowerCase())
          .filter(Boolean)
      ),
    [excludeUserIds]
  );
  const excludedEmailSet = useMemo(
    () =>
      new Set(
        (excludeEmails || [])
          .map(email => String(email || '').trim().toLowerCase())
          .filter(Boolean)
      ),
    [excludeEmails]
  );
  const excludedIdSetRef = useRef(excludedIdSet);
  const excludedEmailSetRef = useRef(excludedEmailSet);
  excludedIdSetRef.current = excludedIdSet;
  excludedEmailSetRef.current = excludedEmailSet;

  const [flaggedUserIdsState, setFlaggedUserIdsState] = useState([]);
  const [flaggedEmailsState, setFlaggedEmailsState] = useState([]);
  const flaggedUserIds =
    flaggedUserIdsProp != null ? flaggedUserIdsProp : flaggedUserIdsState;
  const flaggedEmails =
    flaggedEmailsProp != null ? flaggedEmailsProp : flaggedEmailsState;
  const flaggedIdSet = useMemo(
    () =>
      new Set(
        (flaggedUserIds || [])
          .map(id => String(id || '').trim().toLowerCase())
          .filter(Boolean)
      ),
    [flaggedUserIds]
  );
  const flaggedEmailSet = useMemo(
    () =>
      new Set(
        (flaggedEmails || [])
          .map(email => String(email || '').trim().toLowerCase())
          .filter(Boolean)
      ),
    [flaggedEmails]
  );
  const flaggedIdSetRef = useRef(flaggedIdSet);
  const flaggedEmailSetRef = useRef(flaggedEmailSet);
  flaggedIdSetRef.current = flaggedIdSet;
  flaggedEmailSetRef.current = flaggedEmailSet;

  const isExcludedOption = useCallback(item => {
    const id = String(item?.value || '').trim().toLowerCase();
    const email = String(item?.email || '').trim().toLowerCase();
    const userId = String(getCollaboratorUserId(item) || '')
      .trim()
      .toLowerCase();
    if (id && excludedIdSetRef.current.has(id)) return true;
    if (userId && excludedIdSetRef.current.has(userId)) return true;
    if (email && excludedEmailSetRef.current.has(email)) return true;
    return false;
  }, []);

  const isFlaggedUserId = useCallback(item => {
    if (!item || item.__isNew__) return false;
    if (isUserFlaggedByMe(item?.raw)) return true;
    const userId = String(getCollaboratorUserId(item) || '')
      .trim()
      .toLowerCase();
    const valueId = String(item?.value || '').trim().toLowerCase();
    const email = String(item?.email || item?.raw?.email || '')
      .trim()
      .toLowerCase();
    if (userId && flaggedIdSetRef.current.has(userId)) return true;
    if (valueId && flaggedIdSetRef.current.has(valueId)) return true;
    if (email && flaggedEmailSetRef.current.has(email)) return true;
    return false;
  }, []);

  const withFlaggedState = useCallback(
    item => {
      if (!item || item.__isNew__) return item;
      const flagged = isFlaggedUserId(item);
      return {
        ...item,
        isFlagged: flagged,
        isDisabled: flagged,
      };
    },
    [isFlaggedUserId]
  );

  const isFlaggedOption = useCallback(
    item => Boolean(item?.isFlagged || isFlaggedUserId(item)),
    [isFlaggedUserId]
  );

  const [inputValue, setInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const latestRequest = useRef(0);
  const abortRef = useRef(null);
  const cityIdRef = useRef(cityId);
  const allowCreateRef = useRef(allowCreate);
  const currentUserRef = useRef(currentUser);
  const loadUsersRef = useRef(null);

  cityIdRef.current = cityId;
  allowCreateRef.current = allowCreate;
  currentUserRef.current = currentUser;

  const debouncedSetQuery = useMemo(
    () => debounce(query => setSearchQuery(query), SEARCH_DEBOUNCE_MS),
    []
  );

  const resetSearch = useCallback(() => {
    debouncedSetQuery.cancel();
    setInputValue('');
    setSearchQuery('');
    setOptions([]);
    setNextPageUrl(null);
    setMenuOpen(false);
    setIsLoadingOptions(false);
    setIsLoadingMore(false);
  }, [debouncedSetQuery]);

  const loadUsers = useCallback(
    async ({ query = '', pageUrl = null, isLoadMore = false } = {}) => {
      const activeCityId = cityIdRef.current;
      const canCreate = allowCreateRef.current;

      if (!activeCityId) {
        setOptions([]);
        setNextPageUrl(null);
        return;
      }

      const search = query.trim();

      // Private session picker: members API is search-only (no empty list fetch).
      if (!canCreate && !search && !pageUrl) {
        setOptions([]);
        setNextPageUrl(null);
        return;
      }

      const requestId = ++latestRequest.current;
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      if (isLoadMore) {
        setIsLoadingMore(true);
      } else {
        setIsLoadingOptions(true);
        setIsLoadingMore(false);
      }

      try {
        const response = pageUrl
          ? await get(pageUrl, { signal: controller.signal })
          : canCreate
            ? await get(
                search
                  ? endpoints.searchUsers(activeCityId, search)
                  : endpoints.getUsersByCity(activeCityId),
                {
                  signal: controller.signal,
                  params: {
                    page: 1,
                    page_size: USER_SEARCH_PAGE_SIZE,
                  },
                }
              )
            : await get(endpoints.getCityMembers(activeCityId), {
                signal: controller.signal,
                params: {
                  page: 1,
                  per_page: USER_SEARCH_PAGE_SIZE,
                  search,
                },
              });

        if (requestId !== latestRequest.current) return;

        const result = response?.data?.result || {};
        const list = Array.isArray(result) ? result : result?.data || [];
        const mapped = list
          .filter(user => !isCurrentUser(user, currentUserRef.current))
          .map(user =>
            mapUserToCollabOption(user, {
              isFlagged: (() => {
                const id = String(getUserId(user) || '')
                  .trim()
                  .toLowerCase();
                const email = String(user?.email || '')
                  .trim()
                  .toLowerCase();
                return Boolean(
                  (id && flaggedIdSetRef.current.has(id)) ||
                    (email && flaggedEmailSetRef.current.has(email))
                );
              })(),
            })
          )
          .filter(item =>
            canCreate
              ? Boolean(item.value || item.email)
              : Boolean(asUuid(item.value))
          )
          .filter(item => !isExcludedOption(item));
        const next = Array.isArray(result)
          ? response?.data?.next_page_url
          : result?.next_page_url;

        setOptions(prev => {
          if (!isLoadMore) return mapped;
          const seen = new Set(prev.map(item => String(item.value)));
          return [
            ...prev,
            ...mapped.filter(item => !seen.has(String(item.value))),
          ];
        });
        setNextPageUrl(next || null);
      } catch {
        if (requestId !== latestRequest.current) return;
        if (!isLoadMore) {
          setOptions([]);
          setNextPageUrl(null);
        }
      } finally {
        if (requestId !== latestRequest.current) return;
        if (isLoadMore) {
          setIsLoadingMore(false);
        } else {
          setIsLoadingOptions(false);
        }
      }
    },
    [isExcludedOption]
  );

  loadUsersRef.current = loadUsers;

  useEffect(() => {
    if (!enabled || !menuOpen || !cityId) return undefined;

    const trimmedQuery = searchQuery.trim();
    if (!allowCreate && !trimmedQuery) {
      setOptions([]);
      setNextPageUrl(null);
      setIsLoadingOptions(false);
      return undefined;
    }

    loadUsersRef.current({ query: searchQuery });
    return undefined;
  }, [enabled, menuOpen, cityId, searchQuery, allowCreate]);

  useEffect(() => {
    resetSearch();
  }, [cityId, resetSearch]);

  useEffect(() => {
    setOptions(prev => prev.filter(item => !isExcludedOption(item)));
  }, [excludedIdSet, excludedEmailSet, isExcludedOption]);

  useEffect(() => {
    if (flaggedUserIdsProp != null) return undefined;
    if (!enabled) {
      setFlaggedUserIdsState([]);
      setFlaggedEmailsState([]);
      return undefined;
    }

    let cancelled = false;
    fetchMyFlaggedMembers()
      .then(result => {
        if (cancelled) return;
        setFlaggedUserIdsState(result?.userIds || []);
        setFlaggedEmailsState(result?.emails || []);
      })
      .catch(() => {
        if (cancelled) return;
        setFlaggedUserIdsState([]);
        setFlaggedEmailsState([]);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, flaggedUserIdsProp]);

  useEffect(() => {
    const selected = value || [];
    const nextValue = selected.filter(item => !isFlaggedUserId(item));
    if (nextValue.length !== selected.length) {
      onChange?.(nextValue);
    }
    // Strip flagged chips once flag membership is known.
  }, [flaggedIdSet, flaggedEmailSet, isFlaggedUserId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (enabled) return undefined;
    resetSearch();
    return undefined;
  }, [enabled, resetSearch]);

  useEffect(
    () => () => {
      if (abortRef.current) abortRef.current.abort();
      debouncedSetQuery.cancel();
    },
    [debouncedSetQuery]
  );

  const unavailableReason =
    disabledReason ||
    (!skipCityCheck && !cityId ? 'Select a city first' : null);
  const isDisabled = disabled || Boolean(unavailableReason);

  const flaggedOptions = useMemo(
    () => (options || []).map(withFlaggedState),
    [options, withFlaggedState]
  );

  const canCreateEmailOption = input => {
    if (!allowCreate) return false;
    const email = String(input || '').trim().toLowerCase();
    if (!isValidInviteEmail(email)) return false;
    if (currentUserEmail && email === currentUserEmail) return false;
    if (excludedEmailSet.has(email)) return false;
    const alreadySelected = (value || []).some(
      item => String(item.email || '').trim().toLowerCase() === email
    );
    if (alreadySelected) return false;
    return !options.some(
      item => String(item.email || '').trim().toLowerCase() === email
    );
  };

  const normalizeSelected = (selected = []) =>
    (selected || [])
      .map(item => {
        if (item?.email) return withFlaggedState(item);
        if (allowCreate && (item?.__isNew__ || isValidInviteEmail(item?.value))) {
          return mapEmailToCollabOption(item.value || item.label);
        }
        return withFlaggedState(item);
      })
      .filter(item => {
        const email = String(item.email || '').trim().toLowerCase();
        const id = String(item.value || '');
        if (currentUserEmail && email === currentUserEmail) return false;
        if (currentUserId && id === currentUserId) return false;
        if (isExcludedOption(item)) return false;
        if (isFlaggedOption(item)) return false;
        if (!allowCreate && item?.__isNew__) return false;
        if (!allowCreate) return Boolean(asUuid(id) || asUuid(getCollaboratorUserId(item)));
        return Boolean(email || id);
      });

  const SelectComponent = allowCreate ? CreatableSelect : Select;
  const createSelectProps = allowCreate
    ? {
        allowCreateWhileLoading: true,
        formatCreateLabel: input => `Invite "${String(input || '').trim()}"`,
        isValidNewOption: canCreateEmailOption,
        getNewOptionData: (nextValue, optionLabel) =>
          mapEmailToCollabOption(nextValue || optionLabel),
      }
    : {};

  return (
      <div
        className={
          className || 'project-collab-modal__field'
        }
      >
        <label
          className={labelClassName || 'project-collab-modal__label'}
          htmlFor={inputId}
        >
          {label}
        </label>
        <div
          className={`project-collab-modal__search${
            error ? ' project-collab-modal__search--error' : ''
          }`}
        >
          <img src={ICONS.messageSearchCollab} alt="" />
          <SelectComponent
            inputId={inputId}
            className="project-collab-modal__user-select"
            classNamePrefix="project-collab-modal-user"
            options={flaggedOptions}
            value={value}
            onChange={selected => {
              onChange?.(normalizeSelected(selected));
              setInputValue('');
            }}
            onBlur={onBlur}
            inputValue={inputValue}
            onInputChange={(next, meta) => {
              if (meta.action !== 'input-change') return;
              setInputValue(next);
              debouncedSetQuery(next);
            }}
            isMulti
            isSearchable
            isClearable={false}
            isDisabled={isDisabled}
            isOptionDisabled={option => isFlaggedUserId(option)}
            isLoading={isLoadingOptions}
            menuIsOpen={menuOpen}
            onMenuOpen={() => setMenuOpen(true)}
            onMenuClose={() => setMenuOpen(false)}
            onMenuScrollToBottom={() => {
              if (isLoadingOptions || isLoadingMore || !nextPageUrl) return;
              loadUsers({
                query: searchQuery,
                pageUrl: nextPageUrl,
                isLoadMore: true,
              });
            }}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            {...createSelectProps}
            filterOption={() => true}
            placeholder={placeholder}
            noOptionsMessage={() =>
              unavailableReason ||
              (allowCreate && isValidInviteEmail(inputValue)
                ? 'Press enter to invite this email'
                : !allowCreate && !searchQuery.trim()
                  ? 'Type a name or email to search city members'
                  : searchQuery.trim()
                    ? 'No collaborators found'
                    : 'Type a name or email to search')
            }
            loadingMessage={() => 'Searching...'}
            components={{
              Option: CollaboratorOption,
              MultiValueLabel: CollaboratorMultiValueLabel,
              IndicatorSeparator: null,
              DropdownIndicator: null,
            }}
            styles={collabSelectStyles(Boolean(error), menuZIndex)}
            menuPortalTarget={
              typeof document !== 'undefined' ? document.body : null
            }
            menuPosition="fixed"
            aria-label={label}
          />
        </div>
        {error ? (
          <span className="project-collab-modal__error">{error}</span>
        ) : null}
      </div>
  );
};

export default CollaboratorSearchSelect;
